import { Form, message } from "antd";
import { useEventCreateMutation } from "@ticketingplatform/api/dist/event/hooks";
import { useIntl } from "react-intl";
import { useCallback, useMemo } from "react";
import {
  EventStatus,
  EventType,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { useNavigate, useParams } from "react-router-dom";
import useImageUpload from "hooks/useImageUpload";
import {
  GET_EVENTS,
  GET_EVENT_OCCURRENCES,
  GET_EVENT_OCCURRENCE,
} from "@ticketingplatform/api/dist/event/queries";
import { getEnumValueByStringValue } from "utils/types";
import { ROUTE_PATHS } from "const/paths";
import { useVenueContext } from "contexts/VenueContext";
import { useDiscountCodesQuery } from "@ticketingplatform/api/dist/discountCode";
import { DeepPartial } from "setup/deepPartial";
import useApolloErrorFields from "hooks/useApolloErrorFields";
import usePrompt from "hooks/usePrompt";

import { EventFormProps, EventFormValues } from "../EventForm/types";
import { EVENT_INIT_VALUES } from "../EventForm/consts";

export const useEventForm = (): EventFormProps => {
  const { venue } = useVenueContext();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { type } = useParams();
  const eventType =
    getEnumValueByStringValue(EventType, type?.toUpperCase()) ||
    EventType.TICKETED;
  const intl = useIntl();
  const [createEvent, { loading, error }] = useEventCreateMutation({
    evictQueries: [GET_EVENTS, GET_EVENT_OCCURRENCES, GET_EVENT_OCCURRENCE],
  });
  const fields = useApolloErrorFields(error, true);
  const { uploadImage, isUploadLoading } = useImageUpload();
  const { data: discountData, loading: isDiscountLoading } =
    useDiscountCodesQuery(
      useMemo(
        () =>
          venue?.id
            ? {
                variables: {
                  filters: {
                    venueId: {
                      equals: venue.id,
                    },
                    isApplicableToAllEvents: {
                      equals: true,
                    },
                    OR: [
                      {
                        expireAt: null,
                      },
                      {
                        expireAt: {
                          gt: new Date(),
                        },
                      },
                    ],
                  },
                },
              }
            : {
                skip: true,
              },
        [venue?.id]
      )
    );

  usePrompt(loading || isUploadLoading);

  const initialValues = useMemo<DeepPartial<EventFormValues>>(
    () => ({
      ...EVENT_INIT_VALUES,
      input: {
        ...EVENT_INIT_VALUES.input,
        timezone: venue?.timezone,
        discountCodes: discountData?.discountCodes?.nodes || [],
      },
    }),
    [discountData?.discountCodes?.nodes, venue?.timezone]
  );

  const onFinish = useCallback(
    async (values: EventFormValues) => {
      const {
        input: { image, seatMap, ticketPools, discountCodes, status, ...input },
      } = values;

      try {
        const imageId = await (async () => {
          if (!image) {
            return initialValues?.input?.image ? null : undefined;
          }

          if ("id" in image) {
            return undefined;
          }

          const {
            image: { id },
          } = await uploadImage(image);

          return id;
        })();

        const { data } = await createEvent({
          variables: {
            input: {
              ...input,
              discountCodeIds: discountCodes?.map(({ id }) => id),
              venueId: venue?.id,
              ticketPools: ticketPools
                ? ticketPools.map(({ id: _, ...ticketPool }) => ticketPool)
                : undefined,
              type: eventType,
              status: status || EventStatus.DRAFT,
              seatMapId: seatMap?.id,
              imageId,
            },
          },
        });

        if (!data) {
          return;
        }

        message.success(
          intl.formatMessage({
            defaultMessage: "Event has been added!",
          })
        );

        navigate(ROUTE_PATHS.EventList, {
          state: {
            skipBlock: true,
          },
        });
      } catch {
        message.error(
          intl.formatMessage({
            defaultMessage: "Cannot create event!",
          })
        );
      }
    },
    [
      createEvent,
      eventType,
      initialValues?.input?.image,
      intl,
      navigate,
      uploadImage,
      venue?.id,
    ]
  );

  return {
    eventType,
    loading: loading || isUploadLoading || isDiscountLoading,
    form,
    fields,
    initialValues,
    onFinish,
    eventUrlIds: {
      preview: null,
      eventDetails: null,
    },
  };
};
